import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/Toast";
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from "lucide-react";

/**
 * Gate bloqueante de cuestionarios obligatorios.
 * Si el usuario (no-admin) tiene un cuestionario pendiente, bloquea la app
 * hasta que lo complete y APRUEBE. Se monta globalmente dentro de AuthProvider.
 */
export default function QuizGate() {
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState(false);
  const sendingRef = useRef(false); // Fix BQ: el estado busy tarda un render; el doble toque enviaba dos veces

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/quizzes/pending");
      setQuiz(data.quiz);
      setAnswers(data.quiz ? new Array(data.quiz.questions.length).fill(-1) : []);
      setResult(null);
    } catch { /* silent */ }
    finally { setChecked(true); }
  }, []);

  useEffect(() => {
    if (user && user.role && user.role !== "admin") {
      setChecked(false);
      fetchPending();
    } else {
      setQuiz(null);
      setChecked(true);
    }
  }, [user, fetchPending]);

  if (!user || user.role === "admin" || !checked || !quiz) return null;

  const allAnswered = answers.length > 0 && answers.every((a) => a >= 0);

  const submit = async () => {
    if (!allAnswered || busy || sendingRef.current) return;
    sendingRef.current = true;
    setBusy(true);
    try {
      const { data } = await api.post(`/quizzes/${quiz.id}/submit`, { answers });
      setResult(data);
      if (data.passed) toast.success(`¡Aprobado! ${data.score}%`);
      else toast.error(`No alcanzaste el mínimo (${data.pass_score}%). Reintentá.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); sendingRef.current = false; }
  };

  const retry = () => { setResult(null); setAnswers(new Array(quiz.questions.length).fill(-1)); };
  const continueApp = () => fetchPending(); // busca si hay otro pendiente; si no, cierra

  return (
    <div data-testid="quiz-gate" style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "#0A0A0A",
      overflowY: "auto", color: "#FFFBF4",
    }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 18px 60px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <ShieldAlert size={22} color="#BF3437" />
          <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#BF3437" }}>Capacitación obligatoria</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 900, margin: "0 0 6px", letterSpacing: "-0.02em" }} data-testid="quiz-gate-title">{quiz.title}</h1>
        {quiz.description && <p style={{ fontSize: 13, color: "rgba(255,251,244,0.7)", marginTop: 0 }}>{quiz.description}</p>}
        <p style={{ fontSize: 12, color: "rgba(255,251,244,0.5)", marginBottom: 24 }}>
          Para continuar usando la plataforma debés completar y aprobar este cuestionario (mínimo {quiz.pass_score}%).
        </p>

        {result ? (
          <div data-testid="quiz-gate-result" style={{ textAlign: "center", padding: 28, background: result.passed ? "rgba(22,163,74,0.12)" : "rgba(191,52,55,0.12)", border: `1px solid ${result.passed ? "#16a34a" : "#BF3437"}`, borderRadius: 12 }}>
            {result.passed ? <CheckCircle2 size={48} color="#16a34a" style={{ margin: "0 auto" }}/> : <XCircle size={48} color="#BF3437" style={{ margin: "0 auto" }}/>}
            <div style={{ fontSize: 28, fontWeight: 900, marginTop: 10 }}>{result.score}%</div>
            <div style={{ fontSize: 13, color: "rgba(255,251,244,0.7)" }}>{result.correct}/{result.total} correctas</div>
            <div style={{ fontSize: 14, fontWeight: 800, marginTop: 8, color: result.passed ? "#16a34a" : "#BF3437" }}>
              {result.passed ? "Aprobado" : `No aprobado (mínimo ${result.pass_score}%)`}
            </div>
            {result.passed ? (
              <button onClick={continueApp} className="btn-primary" style={{ marginTop: 18, padding: "12px 24px" }} data-testid="quiz-gate-continue">Continuar a la app</button>
            ) : (
              <button onClick={retry} style={{ marginTop: 18, padding: "12px 24px", background: "#BF3437", color: "#fff", border: "none", borderRadius: 8, fontWeight: 800, cursor: "pointer" }} data-testid="quiz-gate-retry">Reintentar cuestionario</button>
            )}
          </div>
        ) : (
          <>
            {quiz.questions.map((q, i) => (
              <div key={i} style={{ background: "rgba(255,251,244,0.05)", borderRadius: 12, padding: 18, marginBottom: 14 }} data-testid={`quiz-question-${i}`}>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>{i + 1}. {q.text}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {q.options.map((opt, j) => {
                    const sel = answers[i] === j;
                    return (
                      <button key={j} type="button"
                        onClick={() => setAnswers((prev) => prev.map((a, idx) => idx === i ? j : a))}
                        data-testid={`quiz-option-${i}-${j}`}
                        style={{ textAlign: "left", padding: "11px 14px", borderRadius: 8,
                          border: `2px solid ${sel ? "#BF3437" : "rgba(255,251,244,0.15)"}`,
                          background: sel ? "rgba(191,52,55,0.18)" : "transparent",
                          color: "#FFFBF4", fontSize: 13, fontWeight: sel ? 800 : 600, cursor: "pointer" }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={submit} disabled={!allAnswered || busy} className="btn-primary"
              style={{ width: "100%", padding: 14, marginTop: 8, opacity: allAnswered ? 1 : 0.5 }} data-testid="quiz-gate-submit">
              {busy ? <Loader2 size={16} className="animate-spin" style={{ display: "inline" }}/> : null} {busy ? " Enviando..." : "Enviar respuestas"}
            </button>
            {!allAnswered && <div style={{ fontSize: 11, color: "rgba(255,251,244,0.5)", textAlign: "center", marginTop: 8 }}>Respondé todas las preguntas para continuar.</div>}
          </>
        )}
      </div>
    </div>
  );
}
